import { Component, EventEmitter, Inject, Input, OnChanges, OnDestroy, OnInit, Optional, Output, SimpleChanges } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject, ReplaySubject, Subject } from 'rxjs';
import { debounceTime, delay, map, takeUntil, tap } from 'rxjs/operators';
import { Api } from 'src/app/shared/constants/api';
import { KEYS } from 'src/app/shared/constants/keys';
import { DataService } from 'src/app/shared/services/data.service';
import { UtilityService } from 'src/app/shared/services/utility.service';
import { IBranchCreditDetail, IBranchTax } from '../external-entity.interface';
import { ExternalEntityService } from '../external-entity.service';
import { Message } from 'src/app/shared/constants/message';

@Component({
    selector: 'app-branch-gst-details',
    templateUrl: './branch-gst-details.component.html',
    styleUrls: ['./branch-gst-details.component.less'],
    standalone: false
})
export class BranchGstDetailsComponent implements OnInit, OnChanges, OnDestroy {

  constructor(private formBuilder: FormBuilder,
              @Optional() private dailogRef: MatDialogRef<BranchGstDetailsComponent>,
              @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
              private logger: NGXLogger,
              private service: DataService,
              private utilityService: UtilityService,
              private entityService: ExternalEntityService) { }

  @Input() buttonHidden: boolean = false;
  @Input() skipFetch: boolean = false;
  @Input() customerFlag: boolean = false;
  @Input() vendorFlag: boolean = false;
  private _pendingData: any = null;
  private _disableTable: boolean;
  @Input() set disableTable(value: boolean) {
    this._disableTable = value;
    this.applyDisabledState();
  }
  get disableTable(): boolean { return this._disableTable; }

  @Input() set branchApiData(data: any) {
    if (!data) { return; }
    this._pendingData = data;
    if (this.branchTaxDetailForm) { this._applyData(data); }
  }

  @Input() set sharedSegments(segs: any[]) {
    if (segs?.length) { this.businessSegments = segs; }
  }

  @Input() set sharedCurrencies(curs: any[]) {
    if (curs?.length) {
      this.currencyList = curs;
      this.filteredCurrency.next(curs.slice());
    }
  }

  @Input() entityCustomerCurrencyId: number = null;
  @Input() entityCustomerCurrencyName: string = null;
  @Input() entityVendorCurrencyId: number = null;
  @Input() entityVendorCurrencyName: string = null;
  @Output() branchCreditAmountChanged = new EventEmitter<void>();

  private applyDisabledState() {
    if (!this.branchTaxDetailForm) { return; }
    if (this._disableTable) {
      this.branchTaxDetailForm.disable({ emitEvent: false });
      this.branchCreditDetailsForm?.disable({ emitEvent: false });
    } else {
      this.branchTaxDetailForm.enable({ emitEvent: false });
      this.branchCreditDetailsForm?.enable({ emitEvent: false });
    }
  }

  /** Called by parent entity-form after a credit amount field changes,
   *  so the branch validation runs against the latest entity-level limits. */
  refreshCreditValidation() {
    if (this.branchData) {
      this.validateBranchCredit();
    }
  }

  @Input() set branchId(id: number) {
    this.id = id;
    if (this.branchTaxDetailForm && !this.skipFetch) {
      this.getBranchById();
    }
  }

  id: number;
  branchData: any;

  // GST section
  branchGSTList: IBranchTax[] = [];
  branchTaxDetailForm: FormGroup;
  branchGSTDataSource = new BehaviorSubject<any[]>([]);
  gstColumns = ['gstNo', 'description', 'action'];
  get displayedGstColumns(): string[] {
    return this.disableTable ? this.gstColumns.filter(c => c !== 'action') : this.gstColumns;
  }

  // Credit Details section (split into customer / vendor)
  branchCreditList: IBranchCreditDetail[] = [];
  branchCreditDetailsForm: FormGroup;
  branchCustomerCreditDataSource = new BehaviorSubject<any[]>([]);
  branchVendorCreditDataSource = new BehaviorSubject<any[]>([]);
  creditColumns = ['businessSegment', 'creditDays', 'creditAmount', 'currency', 'action'];
  customerCreditSumError: string | null = null;
  vendorCreditSumError: string | null = null;
  get displayedCreditColumns(): string[] {
    return this.disableTable ? this.creditColumns.filter(c => c !== 'action') : this.creditColumns;
  }
  businessSegments: any[] = [];
  currencyList: any[] = [];
  currencySearchCtrl = new FormControl('');
  filteredCurrency: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);
  searching = false;
  onDestroy = new Subject<void>();

  ngOnChanges(changes: SimpleChanges): void {
    const customerChanged = changes['entityCustomerCurrencyId'] || changes['entityCustomerCurrencyName'];
    const vendorChanged = changes['entityVendorCurrencyId'] || changes['entityVendorCurrencyName'];

    if (customerChanged && !changes['entityCustomerCurrencyId']?.firstChange) {
      this.applyEntityCurrencyToExistingRows(
        this.branchCustomerCreditDetails, this.entityCustomerCurrencyId, this.entityCustomerCurrencyName
      );
      this.branchCustomerCreditDataSource.next(this.branchCustomerCreditDetails.value);
    }
    if (vendorChanged && !changes['entityVendorCurrencyId']?.firstChange) {
      this.applyEntityCurrencyToExistingRows(
        this.branchVendorCreditDetails, this.entityVendorCurrencyId, this.entityVendorCurrencyName
      );
      this.branchVendorCreditDataSource.next(this.branchVendorCreditDetails.value);
    }
  }

  private applyEntityCurrencyToExistingRows(rows: FormArray, currencyId: number, currencyName: string): void {
    if (!rows || !currencyId) { return; }
    rows.controls.forEach(ctrl => {
      ctrl.get('currencyId').setValue(currencyId, { emitEvent: false });
      ctrl.get('currencyName').setValue(currencyName, { emitEvent: false });
      ctrl.markAsDirty();
    });
  }

  ngOnInit(): void {
    if (this.data && !this.id) {
      this.id = this.data;
    }
    this.initializeForms();
    this.applyDisabledState();
    if (!this.skipFetch) {
      this.loadBusinessSegments();
      this.loadCurrencies();
    }
    this.filterCurrency();
    if (this._pendingData) {
      this._applyData(this._pendingData);
    } else if (!this.skipFetch && this.id && !isNaN(this.id)) {
      this.getBranchById();
    }
  }

  private _applyData(data: any): void {
    this.branchData = data;
    this.branchGSTList = data.taxDetails || data.taxDetialsDto || [];
    this.branchCreditList = data.branchCreditDetails || [];
    this.setbranchTaxForm();
    this.setbranchCreditForm();
    this.applyDisabledState();
  }

  initializeForms() {
    this.branchTaxDetailForm = this.entityService.getGSTDetailsForm();
    this.branchCreditDetailsForm = this.entityService.getBranchCreditDetailsForm();
  }

  private loadBusinessSegments() {
    const companyId = localStorage.getItem(KEYS.COMPANY_ID);
    if (companyId) {
      this.service.get(`${Api.COMPANY_BUSINESS_SEGMENT_DD}/${companyId}`).subscribe((res: any[]) => {
        if (res) { this.businessSegments = res; }
      });
    }
  }

  private loadCurrencies() {
    this.service.get(Api.CURRENCY_FOR_DD).subscribe((res: any[]) => {
      if (res) {
        this.currencyList = res;
        this.filteredCurrency.next(this.currencyList.slice());
      }
    });
  }

  filterCurrency() {
    this.currencySearchCtrl.valueChanges.pipe(
      tap(() => this.searching = true),
      takeUntil(this.onDestroy),
      debounceTime(200),
      map(search => {
        if (!this.currencyList) { return []; }
        if (!search) { return this.currencyList.slice(); }
        return this.currencyList.filter(c => c.name.toLowerCase().indexOf(search.toLowerCase()) > -1);
      }),
      delay(200)
    ).subscribe(filtered => {
      this.searching = false;
      this.filteredCurrency.next(filtered);
    }, () => { this.searching = false; });
  }

  ngOnDestroy() {
    this.onDestroy.next();
    this.onDestroy.complete();
  }

  setbranchTaxForm() {
    this.taxDetialsDto.clear();
    this.branchGSTList?.filter(data => data.gstNo?.trim())?.forEach(data => {
      this.taxDetialsDto.push(this.entityService.branchTaxForm(data));
    });
    this.branchGSTDataSource.next(this.taxDetialsDto.value);
  }

  setbranchCreditForm() {
    this.branchCustomerCreditDetails.clear();
    this.branchVendorCreditDetails.clear();
    const customerList = (this.branchCreditList || []).filter(d => d.creditType === 'CUSTOMER');
    const vendorList = (this.branchCreditList || []).filter(d => d.creditType === 'VENDOR');
    customerList.forEach(data => this.branchCustomerCreditDetails.push(this.entityService.branchCreditDetailForm(data)));
    vendorList.forEach(data => this.branchVendorCreditDetails.push(this.entityService.branchCreditDetailForm(data)));
    this.branchCustomerCreditDataSource.next(this.branchCustomerCreditDetails.value);
    this.branchVendorCreditDataSource.next(this.branchVendorCreditDetails.value);
  }
  addGSTRow() {
    this.taxDetialsDto.push(this.entityService.branchTaxForm());
    this.taxDetialsDto.markAsDirty();
    this.branchGSTDataSource.next(this.taxDetialsDto.value);
  }

  deleteGSTRow(index: number) {
    if (this.taxDetialsDto.length <= 1) { return; }
    this.taxDetialsDto.removeAt(index);
    this.taxDetialsDto.markAsDirty();
    this.branchGSTDataSource.next(this.taxDetialsDto.value);
  }

  trimFieldValue(index: number, fieldName: string) {
    const ctrl = this.taxDetialsDto.at(index).get(fieldName);
    if (ctrl?.value != null) {
      ctrl.setValue(ctrl.value.trim(), { emitEvent: false });
      ctrl.updateValueAndValidity();
    }
  }

  onGSTNoChange(index: number) {
    const seenGst = new Set<string>();
    this.taxDetialsDto.controls.forEach((row: any) => {
      const ctrl = row.get('gstNo');
      const gst: string = (ctrl?.value || '').trim().toUpperCase();
      if (!gst) { return; }
      if (seenGst.has(gst)) {
        ctrl.setErrors({ ...(ctrl.errors || {}), duplicate: true });
      } else {
        if (ctrl.hasError('duplicate')) {
          const { duplicate, ...rest } = ctrl.errors || {};
          ctrl.setErrors(Object.keys(rest).length ? rest : null);
        }
        seenGst.add(gst);
      }
    });
  }

  onGSTNoBlur(index: number) {
    const ctrl = this.taxDetialsDto.at(index).get('gstNo');
    if (ctrl?.value != null) {
      ctrl.setValue(ctrl.value.trim().toUpperCase(), { emitEvent: false });
      ctrl.updateValueAndValidity();
    }
    this.onGSTNoChange(index);
  }


  addCustomerCreditRow() {
    this.branchCustomerCreditDetails.push(this.entityService.branchCreditDetailForm({
      creditType: 'CUSTOMER',
      currencyId: this.entityCustomerCurrencyId || null,
      currencyName: this.entityCustomerCurrencyName || null
    }));
    this.branchCustomerCreditDetails.markAsDirty();
    this.branchCustomerCreditDataSource.next(this.branchCustomerCreditDetails.value);
  }

  deleteCustomerCreditRow(index: number) {
    this.branchCustomerCreditDetails.removeAt(index);
    this.branchCustomerCreditDetails.markAsDirty();
    this.branchCustomerCreditDataSource.next(this.branchCustomerCreditDetails.value);
  }

  addVendorCreditRow() {
    this.branchVendorCreditDetails.push(this.entityService.branchCreditDetailForm({
      creditType: 'VENDOR',
      currencyId: this.entityVendorCurrencyId || null,
      currencyName: this.entityVendorCurrencyName || null
    }));
    this.branchVendorCreditDetails.markAsDirty();
    this.branchVendorCreditDataSource.next(this.branchVendorCreditDetails.value);
  }

  deleteVendorCreditRow(index: number) {
    this.branchVendorCreditDetails.removeAt(index);
    this.branchVendorCreditDetails.markAsDirty();
    this.branchVendorCreditDataSource.next(this.branchVendorCreditDetails.value);
  }

  onCustomerCreditSegmentSelect(i: number, segId: number) {
    const seg = this.businessSegments.find(s => s.businessSegmentId === segId);
    if (seg) {
      this.branchCustomerCreditDetails.at(i).get('businessSegmentName').setValue(seg.businessSegmentName);
    }
    this.checkCreditDuplicateSegments(this.branchCustomerCreditDetails);
  }

  onVendorCreditSegmentSelect(i: number, segId: number) {
    const seg = this.businessSegments.find(s => s.businessSegmentId === segId);
    if (seg) {
      this.branchVendorCreditDetails.at(i).get('businessSegmentName').setValue(seg.businessSegmentName);
    }
    this.checkCreditDuplicateSegments(this.branchVendorCreditDetails);
  }

  onCustomerCurrencySelect(i: number, currencyId: number) {
    const curr = this.currencyList.find(c => c.id === currencyId);
    if (curr) {
      this.branchCustomerCreditDetails.at(i).get('currencyName').setValue(curr.name);
    }
  }

  onVendorCurrencySelect(i: number, currencyId: number) {
    const curr = this.currencyList.find(c => c.id === currencyId);
    if (curr) {
      this.branchVendorCreditDetails.at(i).get('currencyName').setValue(curr.name);
    }
  }

  private checkCreditDuplicateSegments(array: FormArray) {
    const seenSegments = new Set<number>();
    array.controls.forEach((row: any) => {
      const segId = row.get('businessSegmentId')?.value;
      const ctrl = row.get('businessSegmentId');
      if (!segId) {
        if (ctrl.hasError('duplicate')) {
          const { duplicate, ...rest } = ctrl.errors || {};
          ctrl.setErrors(Object.keys(rest).length ? rest : null);
        }
        return;
      }
      if (seenSegments.has(segId)) {
        ctrl.setErrors({ duplicate: true });
      } else {
        if (ctrl.hasError('duplicate')) {
          const { duplicate, ...rest } = ctrl.errors || {};
          ctrl.setErrors(Object.keys(rest).length ? rest : null);
        }
        seenSegments.add(segId);
      }
    });
  }

  sanitizeInteger(event: any, control: any) {
    const val = event.target.value.replace(/[^0-9]/g, '');
    event.target.value = val;
    control.setValue(val ? +val : null, { emitEvent: false });
    control.updateValueAndValidity();
  }

  sanitizeDecimal(event: any, control: any) {
    let val: string = event.target.value.replace(/[^0-9.]/g, '');
    const parts = val.split('.');
    if (parts.length > 2) { val = parts[0] + '.' + parts.slice(1).join(''); }
    if (parts[1]?.length > 2) { val = parts[0] + '.' + parts[1].substring(0, 2); }
    event.target.value = val;
    control.setValue(val || null, { emitEvent: false });
    control.updateValueAndValidity();
    this.branchCreditAmountChanged.emit();
  }


  validateAll(): boolean {
    if (!this.validateGST()) { return false; }
    if (!this.validateBranchCredit()) { return false; }
    return true;
  }

  private validateGST(): boolean {
    const seenGst = new Set<string>();
    let valid = true;
    this.taxDetialsDto.controls.forEach((row: any) => {
      const ctrl = row.get('gstNo');
      ctrl.markAsTouched();
      const gst: string = (ctrl?.value || '').trim().toUpperCase();
      if (!gst) {
        ctrl.setErrors({ required: true });
        valid = false;
        return;
      }
      if (ctrl.invalid) {
        valid = false;
        return;
      }
      if (seenGst.has(gst)) {
        row.markAllAsTouched();
        ctrl.setErrors({ duplicate: true });
        valid = false;
      } else {
        seenGst.add(gst);
      }
    });
    if (!valid) {
      this.utilityService.notifier(Message.BRANCH_GST_VALIDATION_ERROR, 'error');
    }
    return valid;
  }

  private validateBranchCredit(): boolean {
    this.customerCreditSumError = null;
    this.vendorCreditSumError = null;
    let valid = true;

    const validateArray = (array: FormArray): boolean => {
      const seenSegments = new Set<number>();
      let arrayValid = true;
      array.controls.forEach((row: any) => {
        const segId = row.get('businessSegmentId')?.value;
        row.get('businessSegmentId').markAsTouched();
        if (!segId) {
          row.get('businessSegmentId').setErrors({ required: true });
          arrayValid = false;
        } else if (seenSegments.has(segId)) {
          row.get('businessSegmentId').setErrors({ duplicate: true });
          arrayValid = false;
        } else {
          row.get('businessSegmentId').setErrors(null);
          seenSegments.add(segId);
        }
      });
      return arrayValid;
    };

    if (!validateArray(this.branchCustomerCreditDetails)) { valid = false; }
    if (!validateArray(this.branchVendorCreditDetails)) { valid = false; }

    const customerLimit = parseFloat(this.branchData?.customerCreditAmount) || 0;
    if (customerLimit > 0) {
      const totalCustomer = this.branchCustomerCreditDetails.controls.reduce(
        (sum, row) => sum + (parseFloat(row.get('creditAmount')?.value) || 0), 0);
      if (totalCustomer > customerLimit) {
        this.customerCreditSumError =
          `Total customer credit (${totalCustomer}) must not exceed the entity customer credit amount (${customerLimit})`;
        valid = false;
      }
    }

    const vendorLimit = parseFloat(this.branchData?.vendorCreditAmount) || 0;
    if (vendorLimit > 0) {
      const totalVendor = this.branchVendorCreditDetails.controls.reduce(
        (sum, row) => sum + (parseFloat(row.get('creditAmount')?.value) || 0), 0);
      if (totalVendor > vendorLimit) {
        this.vendorCreditSumError =
          `Total vendor credit (${totalVendor}) must not exceed the entity vendor credit amount (${vendorLimit})`;
        valid = false;
      }
    }

    if (!valid) {
      this.utilityService.notifier(Message.BRANCH_CREDIT_VALIDATION_ERROR, 'error');
    }
    return valid;
  }

  onSave() {
    if (!this.validateAll()) { return; }
    const taxDetails = this.taxDetialsDto.value.filter(e => e.gstNo?.length > 0);
    const creditDetails = [
      ...this.branchCustomerCreditDetails.value.map(r => ({ ...r, creditType: 'CUSTOMER' })),
      ...this.branchVendorCreditDetails.value.map(r => ({ ...r, creditType: 'VENDOR' }))
    ];
    this.branchData.taxDetialsDto = taxDetails;
    this.branchData.branchCreditDetails = creditDetails;
    this.service.put(Api.ENTITY_BRANCH, this.branchData).subscribe(() => {
      this.utilityService.notifier(Message.BRANCH_GST_CREDIT_SAVED, 'success');
      this.dailogRef?.close();
    });
  }

  onCancel() {
    this.dailogRef?.close();
  }

  getBranchById() {
    if (this.id) {
      this.service.get(Api.ENTITY_BRANCH + '/' + this.id).subscribe(res => {
        this.branchData = res;
        this.branchGSTList = res.taxDetails || res.taxDetialsDto || [];
        this.branchCreditList = res.branchCreditDetails || [];
        this.setbranchTaxForm();
        this.setbranchCreditForm();
        this.applyDisabledState();
      });
    }
  }

  get taxDetialsDto() { return this.branchTaxDetailForm.get('taxDetialsDto') as FormArray; }
  get branchCustomerCreditDetails() { return this.branchCreditDetailsForm.get('branchCustomerCreditDetails') as FormArray; }
  get branchVendorCreditDetails() { return this.branchCreditDetailsForm.get('branchVendorCreditDetails') as FormArray; }
}
